import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPlus, faSpinner, faCheckCircle, faExclamationCircle, faTimes } from '@fortawesome/free-solid-svg-icons';

export default function CategoryManagement() {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [formData, setFormData] = useState({ name: '' });
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            const response = await fetch('/api/categories', {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                }
            });
            if (!response.ok) {
                const contentType = response.headers.get("content-type");
                if (contentType && contentType.includes("application/json")) {
                    const errorData = await response.json();
                    throw new Error(
                        errorData.details ||
                        errorData.error ||
                        'Failed to fetch categories'
                    );
                } else {
                    throw new Error('Server error. Please try again later.');
                }
            }
            const data = await response.json();
            setCategories(data);
        } catch (error) {
            setError({
                type: 'error',
                message: error.message === 'Failed to fetch'
                    ? 'Network error. Please check your connection.'
                    : error.message
            });
            console.error('Error fetching categories:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value.trim()
        });
        setError(null); // Clear any previous errors when input changes
    };

    const openModal = (category = null) => {
        if (category) {
            setFormData({ name: category.name });
            setEditingCategory(category);
        } else {
            setFormData({ name: '' });
            setEditingCategory(null);
        }
        setError(null);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setFormData({ name: '' });
        setEditingCategory(null);
        setError(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name) {
            setError({
                type: 'error',
                message: 'Category name is required'
            });
            return;
        }

        setActionLoading(true);
        try {
            const url = editingCategory
                ? `/api/categories/${editingCategory.id}`
                : '/api/categories';

            const method = editingCategory ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            if (!response.ok) {
                const contentType = response.headers.get("content-type");
                if (contentType && contentType.includes("application/json")) {
                    const errorData = await response.json();
                    throw new Error(
                        errorData.details ||
                        errorData.error ||
                        'Failed to save category'
                    );
                } else {
                    throw new Error('Server error. Please try again later.');
                }
            }

            await fetchCategories();
            closeModal();
            setError({
                type: 'success',
                message: `Category successfully ${editingCategory ? 'updated' : 'created'}`
            });
            setTimeout(() => setError(null), 3000);
        } catch (error) {
            setError({
                type: 'error',
                message: error.message === 'Failed to fetch'
                    ? 'Network error. Please check your connection.'
                    : error.message
            });
            console.error('Error saving category:', error);
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async (id, name) => {
        if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
            setActionLoading(true);
            try {
                const response = await fetch(`/api/categories/${id}`, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (!response.ok) {
                    const contentType = response.headers.get("content-type");
                    if (contentType && contentType.includes("application/json")) {
                        const errorData = await response.json();
                        throw new Error(
                            errorData.details ||
                            errorData.error ||
                            'Failed to delete category'
                        );
                    } else {
                        throw new Error('Server error. Please try again later.');
                    }
                }

                await fetchCategories();
                setError({
                    type: 'success',
                    message: `Category "${name}" was successfully deleted.`
                });
                setTimeout(() => setError(null), 3000);
            } catch (error) {
                setError({
                    type: 'error',
                    message: error.message === 'Failed to fetch'
                        ? 'Network error. Please check your connection.'
                        : error.message
                });
                console.error('Error deleting category:', error);
            } finally {
                setActionLoading(false);
            }
        }
    };

    if (loading) {
        return (
            <div className="loading-container">
                <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                <p>Loading categories...</p>
            </div>
        );
    }

    return (
        <div className="category-management">
            {error && !isModalOpen && (
                <div className={`message-box ${error.type}`}>
                    <span>
                        <FontAwesomeIcon
                            icon={error.type === 'success' ? faCheckCircle : faExclamationCircle}
                        />
                        {error.message}
                    </span>
                    <button onClick={() => setError(null)}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
            )}

            <div className="management-header">
                <h2>Category Management</h2>
                <button
                    className="add-btn"
                    onClick={() => openModal()}
                    disabled={actionLoading}
                >
                    <FontAwesomeIcon icon={faPlus} /> Add Category
                </button>
            </div>

            <div className="management-grid">
                {categories.length === 0 ? (
                    <div className="no-data-message">
                        No categories found. Click "Add Category" to create one.
                    </div>
                ) : (
                    categories.map(category => (
                        <div key={category.id} className="management-card">
                            <div className="card-content">
                                <h3>{category.name}</h3>
                                <p className="date-info">
                                    Created: {new Date(category.createdAt).toLocaleDateString()}
                                    <br />
                                    Updated: {new Date(category.updatedAt).toLocaleDateString()}
                                </p>
                            </div>
                            <div className="card-actions">
                                <button
                                    className="edit-btn"
                                    onClick={() => openModal(category)}
                                    disabled={actionLoading}
                                    title="Edit category"
                                >
                                    <FontAwesomeIcon icon={faEdit} />
                                </button>
                                <button
                                    className="delete-btn"
                                    onClick={() => handleDelete(category.id, category.name)}
                                    disabled={actionLoading}
                                    title="Delete category"
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {isModalOpen && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <h3>{editingCategory ? 'Edit Category' : 'Add New Category'}</h3>
                        {error && <div className="modal-error">{error}</div>}
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label htmlFor="name">Category Name</label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    required
                                    disabled={actionLoading}
                                    placeholder="Enter category name"
                                />
                            </div>
                            <div className="modal-actions">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    disabled={actionLoading}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={actionLoading}
                                >
                                    {actionLoading ? (
                                        <>
                                            <FontAwesomeIcon icon={faSpinner} spin />
                                            {' '}
                                            {editingCategory ? 'Updating...' : 'Creating...'}
                                        </>
                                    ) : (
                                        editingCategory ? 'Update' : 'Create'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
} 